import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, LogOut, Users, PackagePlus, Ticket, Gift, ArrowUpRight, Sparkles, AlertTriangle, Layers } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, getToken, setToken, clearToken } from "@/lib/api";
import DropsAdmin from "@/components/admin/DropsAdmin";
import PaymentsAdmin from "@/components/admin/PaymentsAdmin";
import SpecialDropsAdmin from "@/components/admin/SpecialDropsAdmin";
import DropFormDialog from "@/components/admin/DropFormDialog";
import SpecialDropFormDialog from "@/components/admin/SpecialDropFormDialog";
import HigherChooserDialog from "@/components/admin/HigherChooserDialog";

const ACTIONS = [
  { id: "admin-action-manage-joiners", label: "Manage Joiners", hint: "Add, edit and view joiners", icon: Users, tab: "joiners", tone: "bg-violet-500/15 text-violet-300" },
  { id: "admin-action-add-drop", label: "Add a Drop", hint: "Create a new store drop", icon: PackagePlus, tab: "drops", newDrop: true, tone: "bg-pink-500/15 text-pink-300" },
  { id: "admin-action-add-special-drop", label: "Add a Special Drop", hint: "Merch, Snapism & Washi Tape", icon: Sparkles, tab: "special", newSpecial: true, tone: "bg-orange-500/15 text-orange-300" },
  { id: "admin-action-raffle-logs", label: "Fancall Raffle Logs", hint: "Entries per joiner & winners", icon: Ticket, tab: "raffles", tone: "bg-amber-500/15 text-amber-300" },
  { id: "admin-action-inclusion-tracker", label: "Inclusion Tracker", hint: "Inclusions owed & sent", icon: Gift, tab: "inclusions", tone: "bg-emerald-500/15 text-emerald-300" },
  { id: "admin-action-past-due", label: "Past Due Offenders", hint: "Late payments per joiner", icon: AlertTriangle, tab: "pastdue", badgeKey: "pastDue", tone: "bg-red-500/15 text-red-300" },
  { id: "admin-action-yani-higher", label: "Yani - Higher", hint: "Add Higher drops & joiners", icon: Layers, tab: "higher", badgeKey: "higherPending", tone: "bg-sky-500/15 text-sky-300" },
];

const ActionButtons = ({ onPick, badges = {} }) => (
  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-8" data-testid="admin-actions">
    {ACTIONS.map((a, i) => (
      <button
        key={a.id}
        data-testid={a.id}
        onClick={() => onPick(a)}
        style={{ animationDelay: `${i * 60}ms` }}
        className="rise group text-left rounded-2xl border border-white/10 bg-zinc-900/70 p-4 sm:p-5 transition-[transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-white/25"
      >
        <div className="flex items-start justify-between">
          <span className={`h-10 w-10 rounded-xl grid place-items-center ${a.tone}`}><a.icon className="h-5 w-5" /></span>
          <span className="flex items-center gap-2">
            {a.badgeKey && badges[a.badgeKey] > 0 && <span data-testid={`${a.id}-badge`} className="rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-bold text-white">{badges[a.badgeKey]}</span>}
            <ArrowUpRight className="h-4 w-4 text-zinc-600 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white" />
          </span>
        </div>
        <div className="font-display font-bold text-base sm:text-lg mt-4 leading-tight">{a.label}</div>
        <div className="text-xs text-zinc-500 mt-1">{a.hint}</div>
      </button>
    ))}
  </div>
);

const Login = ({ onIn }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/login", { password: pw });
      setToken(r.data.token);
      onIn();
    } catch (err) {
      toast.error(errMsg(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="rise max-w-sm mx-auto mt-10 rounded-3xl border border-white/10 bg-zinc-900/70 p-8" data-testid="admin-login-form">
      <span className="h-12 w-12 rounded-2xl bg-violet-500/15 text-violet-300 grid place-items-center"><Lock className="h-5 w-5" /></span>
      <h1 className="font-display text-3xl font-extrabold tracking-tight mt-5">Admin access</h1>
      <p className="text-sm text-zinc-400 mt-1">Enter the admin password to manage drops, joiners and payments.</p>
      <Input type="password" autoFocus data-testid="admin-password-input" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" className="mt-6 h-11 bg-zinc-950" />
      <Button type="submit" disabled={busy || !pw} data-testid="admin-login-button" className="w-full h-11 mt-3 rounded-xl bg-white text-zinc-950 hover:bg-zinc-200 font-semibold">Log in</Button>
    </form>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("drops");
  const [badges, setBadges] = useState({});
  useEffect(() => {
    if (authed) {
      api.get("/admin/past-due").then((r) => setBadges((b) => ({ ...b, pastDue: r.data.length }))).catch(() => {});
      api.get("/higher/payments").then((r) => setBadges((b) => ({ ...b, higherPending: r.data.filter((p) => p.status === "pending").length }))).catch(() => {});
    }
  }, [authed]);
  const [newDrop, setNewDrop] = useState(false);
  const [dropsKey, setDropsKey] = useState(0);
  const [newSpecial, setNewSpecial] = useState(false);
  const [specialKey, setSpecialKey] = useState(0);
  const [higher, setHigher] = useState(false);
  const tabsRef = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!getToken()) return setAuthed(false);
    api.get("/admin/verify").then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);
  const logout = () => { clearToken(); setAuthed(false); };
  const pick = (a) => {
    if (a.tab === "joiners") return navigate("/admin/joiners");
    if (a.tab === "inclusions") return navigate("/inclusions");
    if (a.tab === "raffles") return navigate("/admin/raffles");
    if (a.tab === "pastdue") return navigate("/admin/past-due");
    if (a.tab === "higher") return setHigher(true);
    setTab(a.tab);
    if (a.newDrop) setNewDrop(true);
    else if (a.newSpecial) setNewSpecial(true);
    else tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (authed === null) return <div className="text-zinc-500 text-sm">Loading…</div>;
  if (!authed) return <Login onIn={() => setAuthed(true)} />;

  return (
    <div className="rise">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Admin</h1>
          <p className="text-zinc-400 mt-2 text-sm md:text-base">Run drops, manage joiners and review payment proof.</p>
        </div>
        <Button variant="outline" onClick={logout} data-testid="admin-logout-button" className="rounded-xl border-zinc-700 bg-transparent">
          <LogOut className="h-4 w-4 mr-1.5" /> Log out
        </Button>
      </div>
      <ActionButtons onPick={pick} badges={badges} />
      <Tabs value={tab} onValueChange={setTab} className="mt-8 scroll-mt-24" ref={tabsRef}>
        <TabsList className="bg-zinc-900 border border-white/5 h-11 p-1 rounded-xl max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="drops" data-testid="admin-tab-drops" className="rounded-lg">Drops</TabsTrigger>
          <TabsTrigger value="special" data-testid="admin-tab-special" className="rounded-lg">Special Drops</TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-payments" className="rounded-lg">Payments</TabsTrigger>
        </TabsList>
        <TabsContent value="drops" className="mt-6"><DropsAdmin refreshKey={dropsKey} /></TabsContent>
        <TabsContent value="special" className="mt-6"><SpecialDropsAdmin refreshKey={specialKey} /></TabsContent>
        <TabsContent value="payments" className="mt-6"><PaymentsAdmin /></TabsContent>
      </Tabs>
      {newDrop && <DropFormDialog drop={{}} onClose={() => setNewDrop(false)} onSaved={() => setDropsKey((k) => k + 1)} />}
      {higher && <HigherChooserDialog onClose={() => setHigher(false)} pending={badges.higherPending || 0} onReview={() => navigate("/higher#ems-payments")} />}
      {newSpecial && <SpecialDropFormDialog sd={{}} onClose={() => setNewSpecial(false)} onSaved={() => setSpecialKey((k) => k + 1)} />}
    </div>
  );
}
